/* eslint-disable */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { supabase } from "./utils/supabaseClient";
import { COLORS } from "./styles/colors";

const dateFormatter = new Intl.DateTimeFormat("en-SG", { dateStyle: "full" });
const timeFormatter = new Intl.DateTimeFormat("en-SG", { timeStyle: "short" });

const formatEventDateOnly = (event) => {
  if (!event?.event_date) return "Date to be announced";
  return dateFormatter.format(new Date(`${event.event_date}T00:00:00`));
};

const formatEventTimeRange = (event) => {
  if (!event?.event_time) return null;

  const startTime = timeFormatter.format(new Date(`${event.event_date}T${event.event_time}`));

  if (event.event_end_time) {
    const endTime = timeFormatter.format(new Date(`${event.event_date}T${event.event_end_time}`));
    return `${startTime} – ${endTime}`;
  }

  return startTime;
};

const formatEventDate = (event) => {
  const dateLabel = formatEventDateOnly(event);
  const timeRange = formatEventTimeRange(event);
  return timeRange ? `${dateLabel}, ${timeRange}` : dateLabel;
};

const SOZCODE_LOGO_URL = "https://portal.sozcode.com/sozcode-logo.png";

const buildRsvpEmailHtml = ({ event, rsvpName }) => `
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background-color:#F4EEEA;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F4EEEA;padding:40px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;">

          <!-- Header -->
          <tr>
            <td style="background-color:#000000;border-radius:12px 12px 0 0;padding:20px 32px;text-align:center;">
              <a href="https://portal.sozcode.com" style="text-decoration:none;">
                <img src="${SOZCODE_LOGO_URL}" alt="Sozcode" width="140" style="display:inline-block;height:auto;border:0;" />
              </a>
            </td>
          </tr>

          <!-- Card -->
          <tr>
            <td style="background-color:#ffffff;border-radius:0 0 12px 12px;overflow:hidden;box-shadow:0 2px 8px rgba(14,23,53,0.08);">

              <!-- Accent bar -->
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr><td style="height:4px;background-color:#F17946;"></td></tr>
              </table>

              <!-- Body -->
              <table width="100%" cellpadding="0" cellspacing="0" style="padding:36px 40px;">
                <tr>
                  <td>
                    <!-- Badge -->
                    <div style="display:inline-block;background-color:#FEF0E9;color:#F17946;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 10px;border-radius:4px;margin-bottom:20px;">Event RSVP</div>

                    <!-- Headline -->
                    <h1 style="margin:0 0 8px 0;font-size:22px;font-weight:700;color:#0E1735;line-height:1.3;">You're confirmed!</h1>
                    <p style="margin:0 0 28px 0;font-size:15px;color:#7481AF;">Hi ${rsvpName}, your spot is reserved.</p>

                    <!-- Divider -->
                    <hr style="border:none;border-top:1px solid #F0EBE7;margin:0 0 28px 0;">

                    <!-- Event details -->
                    <table width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding-bottom:16px;">
                          <div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#7481AF;margin-bottom:4px;">Event</div>
                          <div style="font-size:16px;font-weight:600;color:#0E1735;">${event.name}</div>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding-bottom:16px;">
                          <div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#7481AF;margin-bottom:4px;">Date</div>
                          <div style="font-size:15px;color:#0E1735;">${formatEventDateOnly(event)}</div>
                        </td>
                      </tr>
                      ${formatEventTimeRange(event) ? `
                      <tr>
                        <td style="padding-bottom:16px;">
                          <div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#7481AF;margin-bottom:4px;">Time</div>
                          <div style="font-size:15px;color:#0E1735;">${formatEventTimeRange(event)}</div>
                        </td>
                      </tr>` : ""}
                      ${event.location ? `
                      <tr>
                        <td style="padding-bottom:16px;">
                          <div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#7481AF;margin-bottom:4px;">Location</div>
                          <div style="font-size:15px;color:#0E1735;">${event.location}</div>
                        </td>
                      </tr>` : ""}
                    </table>

                    <!-- Divider -->
                    <hr style="border:none;border-top:1px solid #F0EBE7;margin:28px 0;">

                    <p style="margin:0;font-size:14px;color:#7481AF;line-height:1.6;">
                      Questions? Reply to this email or reach us at
                      <a href="mailto:courses@sozcode.com" style="color:#F17946;text-decoration:none;">courses@sozcode.com</a>.
                    </p>
                  </td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding-top:24px;text-align:center;">
              <p style="margin:0;font-size:12px;color:#7481AF;">&copy; ${new Date().getFullYear()} Sozcode. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`;

export const EventPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventError, setEventError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      setEventError("");

      const { data, error } = await supabase
        .from("events")
        .select("slug, name, description, event_date, event_time, event_end_time, timezone, location, image_url, capacity, status, metadata")
        .eq("slug", slug)
        .eq("status", "published")
        .single();

      if (error) {
        setEventError("We could not find that event. Please check the link and try again.");
      } else {
        setEvent(data);
      }

      setLoading(false);
    };

    fetchEvent();
  }, [slug]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    setSubmitting(true);
    setSubmitMessage(null);

    const normalizedEmail = form.email.trim().toLowerCase();

    const { error } = await supabase.from("event_rsvps").insert({
      event_slug: slug,
      name: form.name.trim(),
      email: normalizedEmail,
      phone: form.phone.trim() || null,
      notes: form.notes.trim() || null,
    });

    if (error) {
      const duplicateRsvp = error.code === "23505";
      setSubmitMessage({
        status: duplicateRsvp ? "warning" : "error",
        text: duplicateRsvp
          ? "It looks like this email has already RSVP'd for this event."
          : "Sorry, we could not submit your RSVP. Please try again.",
      });
      setSubmitting(false);
      return;
    }

    const { error: emailError } = await supabase.functions.invoke("send-email-webhook", {
      body: {
        title: `RSVP confirmation: ${event.name}`,
        html: buildRsvpEmailHtml({ event, rsvpName: form.name.trim() }),
        recipient: normalizedEmail,
      },
    });

    setSubmitMessage({
      status: emailError ? "warning" : "success",
      text: emailError
        ? "Your RSVP was saved, but the confirmation email could not be sent."
        : "Thanks! Your RSVP has been received and a confirmation email is on its way.",
    });
    setForm({ name: "", email: "", phone: "", notes: "" });
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Container minH="70vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner />
      </Container>
    );
  }

  if (eventError) {
    return (
      <Container maxW="720px" py={12}>
        <Alert status="error">
          <AlertIcon />
          {eventError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW={{ base: "920px", lg: "1120px" }} py={{ base: 8, lg: 12 }} px={{ base: 4, md: 6 }}>
      <Stack
        direction={{ base: "column", lg: "row" }}
        spacing={{ base: 8, lg: 10 }}
        align={{ base: "stretch", lg: "flex-start" }}
      >
        <Box
          flex={{ lg: "3 1 0" }}
          minW={0}
          w="full"
          bg="white"
          borderRadius="lg"
          boxShadow="md"
          overflow="hidden"
        >
          {event.image_url && (
            <Box
              minH={{ base: "220px", lg: "240px" }}
              bgImage={`url(${event.image_url})`}
              bgPosition="center"
              bgSize="cover"
            />
          )}
          <Stack spacing={4} p={{ base: 6, lg: 8 }}>
            <Badge alignSelf="flex-start" colorScheme="orange">Event</Badge>
            <Heading size="md" color={COLORS.maastrichtBlue}>{event.name}</Heading>
            <Text fontSize="lg" color={COLORS.shadowBlue}>{formatEventDate(event)}</Text>
            {event.location && <Text fontWeight="semibold">{event.location}</Text>}
            {event.description && <Text whiteSpace="pre-line">{event.description}</Text>}
          </Stack>
        </Box>

        <Box
          flex={{ lg: "2 1 0" }}
          minW={0}
          w="full"
          bg="white"
          borderRadius="lg"
          boxShadow="md"
          p={{ base: 6, lg: 8 }}
        >
          <Stack as="form" spacing={5} onSubmit={handleSubmit}>
            <Heading size="sm" color={COLORS.maastrichtBlue}>RSVP for this event</Heading>
            {submitMessage && (
              <Alert status={submitMessage.status}>
                <AlertIcon />
                {submitMessage.text}
              </Alert>
            )}
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input value={form.name} onChange={handleChange("name")} placeholder="Your full name" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input type="email" value={form.email} onChange={handleChange("email")} placeholder="you@example.com" />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input value={form.phone} onChange={handleChange("phone")} placeholder="Optional" />
            </FormControl>
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea value={form.notes} onChange={handleChange("notes")} placeholder="Optional notes for the Sozcode team" />
            </FormControl>
            <Button
              type="submit"
              isLoading={submitting}
              bg={COLORS.mandarin}
              color="white"
              _hover={{ bg: "#e06835" }}
            >
              Submit RSVP
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
